import { NextImage, useAccountContext, useDate } from '@apeiron/temp'
import { Box, Button, styled, Typography } from '@mui/material'
import AddPNG from '@public/icons/icon-battle-demo-add.png'
import MinuePNG from '@public/icons/icon-battle-demo-minus.png'
import BurnPreviewSection from '@src/components/battle/demo/pass/BurnPreviewSection'
import ApeironGradientBox from '@src/components/share/apeiron/ApeironGradientBox'
import ApeironI18nList from '@src/components/share/apeiron/ApeironI18nList'
import { BURN_MAX_LIMIT, BURN_MIN_LIMIT } from '@src/constants/battleDemo'
import { useBattlepassContext } from '@src/contexts/battle/demo/BattlepassContext'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TitleContainer = styled(ApeironGradientBox)`
  display: flex;
  padding: 20px 0px;
  ${props => props.theme.breakpoints.down('res1024')} {
    background: unset;
  }
`

const Title = styled(Typography)`
  font-weight: 300;

  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 20px;
    line-height: 20px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 16px;
    line-height: 16px;
  }
`

const PreviewContainer = styled(Box)`
  display: flex;
  gap: 20px;
  justify-content: center;
`

const CommonPreviewSection = styled(BurnPreviewSection)`
  width: 180px;
`

const BootyPreviewSection = styled(CommonPreviewSection)`
  background-color: #8cff3211;
  border: 2px solid #33ff00;
  .Custom-Extra-Value {
    color: #8cff32;
  }
`

const Explain = styled(ApeironI18nList)`
  .MuiTypography-root {
    font-size: 14px;
  }
`

const OperationContainer = styled(Box)`
  display: flex;
  gap: 30px;
  justify-content: center;
`

const OperateButton = styled(Button)`
  padding: 0px;
`

const OperateIcon = styled(NextImage)`
  aspect-ratio: 1;
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 75px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 60px;
  }
`

const BurnPlaytime = styled(Typography)`
  font-weight: 500;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 80px;
    line-height: 80px;
    width: 95px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 60px;
    line-height: 60px;
    width: 65px;
  }
`

const BurnSection: FC<Props> = props => {
  const { className } = props

  const { account } = useAccountContext()

  const { burnCount, setBurnCount } = useBattlepassContext()

  const { textTimeWithSeparator } = useDate()

  const { t } = useTranslation()

  const handleOnAdd = useCallback(() => {
    const nextCount = burnCount + 1

    setBurnCount(Math.min(nextCount, BURN_MAX_LIMIT))
  }, [burnCount, setBurnCount])

  const handleOnMinus = useCallback(() => {
    const nextCount = burnCount - 1

    setBurnCount(Math.max(nextCount, BURN_MIN_LIMIT))
  }, [burnCount, setBurnCount])

  const remainTime = useMemo(() => {
    const remainSeconds = R.pathOr(
      0,
      ['game', 'battlePass', 'booty', 'remainTime'],
      account
    )

    return textTimeWithSeparator({ duration: remainSeconds * 1000 }, ':', ':')
  }, [account, textTimeWithSeparator])

  return (
    <Container className={className}>
      <TitleContainer>
        <Title>{t('battle_demo.pass_name')}</Title>
      </TitleContainer>
      <PreviewContainer>
        <BootyPreviewSection
          explain={<Explain textKey='battle_demo.booty_explain' />}
          extraValue={`${burnCount}:00:00`}
          title={t('battle_demo.booty_left')}
          tooltipId={'booty-tooltips'}
          value={remainTime}
        />
      </PreviewContainer>
      <OperationContainer>
        <OperateButton onClick={handleOnMinus}>
          <OperateIcon src={MinuePNG} />
        </OperateButton>
        <BurnPlaytime>{burnCount}</BurnPlaytime>
        <OperateButton onClick={handleOnAdd}>
          <OperateIcon src={AddPNG} />
        </OperateButton>
      </OperationContainer>
    </Container>
  )
}

type Props = {
  className?: string
}

export default BurnSection
